import React, { useState } from 'react';
import type { Client, Program, CreateCheckinInput, Checkin } from '../../types';
import { Button } from '../shared';
import styles from './CheckinForm.module.css';

interface CheckinFormProps {
  client: Client;
  programs: Program[];
  onSubmit: (input: CreateCheckinInput) => Promise<Checkin>;
  onCancel: () => void;
  isLoading: boolean;
}

export const CheckinForm: React.FC<CheckinFormProps> = ({
  client,
  programs,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [result, setResult] = useState<Checkin | null>(null);
  const [formData, setFormData] = useState<{
    programId: string;
    weekNumber: number;
    energyScore: number;
    difficultyScore: number;
    sorenessScore: number;
    comment: string;
  }>({
    programId: programs[0]?.id || '',
    weekNumber: 1,
    energyScore: 3,
    difficultyScore: 3,
    sorenessScore: 3,
    comment: '',
  });

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const checkinResult = await onSubmit({
      clientId: client.id,
      ...formData,
    });
    setResult(checkinResult);
  };

  const renderScoreButtons = (
    value: number,
    onChange: (val: number) => void,
    lowLabel: string,
    highLabel: string
  ) => (
    <div className={styles.scoreButtons}>
      {[1, 2, 3, 4, 5].map((score) => (
        <button
          key={score}
          type="button"
          className={`${styles.scoreButton} ${value === score ? styles.active : ''}`}
          onClick={() => onChange(score)}
          disabled={isLoading}
        >
          {score}
        </button>
      ))}
      <div className={styles.scoreLabels}>
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );

  if (result) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>✅ Check-in submitted!</h2>
          <p className={styles.subtitle}>for {client.name}</p>
        </div>

        <div className={styles.successContainer}>
          <div className={styles.aiRecommendation}>
            <span className={styles.aiLabel}>🤖 AI Recommendation:</span>
            <p className={styles.aiText}>{result.aiAdjustment}</p>
          </div>

          <Button variant="primary" fullWidth onClick={onCancel}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onCancel}>
          ← Back
        </button>
        <h2 className={styles.title}>Weekly Check-in</h2>
        <p className={styles.subtitle}>for {client.name}</p>
      </div>

      {programs.length === 0 ? (
        <div className={styles.noProgram}>
          <p>No active programs found. Generate a program first.</p>
          <Button variant="secondary" onClick={onCancel}>
            Go Back
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.selectGroup}>
            <label className={styles.selectLabel}>Program</label>
            <select
              className={styles.select}
              value={formData.programId}
              onChange={(e) => setFormData(prev => ({ ...prev, programId: e.target.value }))}
              disabled={isLoading}
            >
              {programs.map((prog, idx) => (
                <option key={prog.id} value={prog.id}>
                  Program {programs.length - idx} - {new Date(prog.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>Week Number</label>
            <input
              type="number"
              className={styles.numberInput}
              value={formData.weekNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, weekNumber: parseInt(e.target.value) || 1 }))}
              min={1}
              max={52}
              disabled={isLoading}
            />
          </div>

          <div className={styles.scoreGroup}>
            <label className={styles.scoreLabel}>Energy Level</label>
            {renderScoreButtons(
              formData.energyScore,
              (val) => setFormData(prev => ({ ...prev, energyScore: val })),
              'Low',
              'High'
            )}
          </div>

          <div className={styles.scoreGroup}>
            <label className={styles.scoreLabel}>Workout Difficulty</label>
            {renderScoreButtons(
              formData.difficultyScore,
              (val) => setFormData(prev => ({ ...prev, difficultyScore: val })),
              'Easy',
              'Hard'
            )}
          </div>

          <div className={styles.scoreGroup}>
            <label className={styles.scoreLabel}>Muscle Soreness</label>
            {renderScoreButtons(
              formData.sorenessScore,
              (val) => setFormData(prev => ({ ...prev, sorenessScore: val })),
              'None',
              'Extreme'
            )}
          </div>

          <div className={styles.textareaGroup}>
            <label className={styles.textareaLabel}>Additional Comments (optional)</label>
            <textarea
              className={styles.textarea}
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="How is the client progressing? Any issues or observations?"
              rows={4}
              disabled={isLoading}
            />
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Submit Check-in
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
